import { types, Instance, flow, getRoot } from "mobx-state-tree"
import { RootStoreModel } from "~models/root-store"
import { globalToast, globalSpinner } from "~utils"

export const SettingStoreModel = types
  .model("SettingStore", {
    // ===== 安全设置 =====
    
    loginNotify: types.optional(types.boolean, false), // 登录通知
    deviceVerify: types.optional(types.boolean, false), // 设备验证
    doubleVerify: types.optional(types.boolean, false), // 双重验证
    // null 表示尚未由用户显式设置，默认值由 walletStore.friend_verify 注入
    friendVerify: types.optional(types.maybeNull(types.boolean), null), // 好友验证
    showOnlineStatus: types.optional(types.boolean, true), // 显示在线状态
    // ===== 消息通知相关 =====
    messageNotify: types.optional(types.boolean, true),   // 消息通知（总开关）
    notifyPreview: types.optional(types.boolean, true),   // 通知预览
    groupNotify: types.optional(types.boolean, true),     // 群聊通知
    channelNotify: types.optional(types.boolean, true), // 频道通知
    soundNotify: types.optional(types.boolean, true),     // 声音通知
    vibrationNotify: types.optional(types.boolean, true), // 震动提醒
    doNotDisturb: types.optional(types.boolean, false),   // 免打扰模式
    // ===== 聊天设置 =====
    enterToSend: types.optional(types.boolean, true),       // 回车键发送
    typingStatus: types.optional(types.boolean, true),     // 输入状态显示
    readReceipt: types.optional(types.boolean, true),      // 已读回执
    enableEmoji: types.optional(types.boolean, true),      // 启用表情符号
    enableSticker: types.optional(types.boolean, true),    // 启用贴纸
    messageSelfDestruct: types.optional(types.boolean, false), // 消息定时自毁
  })
  .actions((self) => {
    const setLoginNotify = (val: boolean) => {
      self.loginNotify = val
    }

    /** 个人中心设置：请求 + 刷新钱包信息 + 统一错误处理 */
    const applyPersonalCenterSetting = flow(function* (params: Record<string, string | number | boolean>) {
      const { apiStore, walletStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      try {
        globalSpinner.show()
        const res = yield apiStore.setPersonalCenterSetting(params)
        walletStore.getWalletInfo()
        walletStore.getMessageDestroySetting()
        globalSpinner.close()
        return res
      } catch (e) {
        globalSpinner.close()
        globalToast.show("设置失败，请稍后重试")
        return { code: -1 }
      }
    })

    const setDeviceVerify = flow(function* (val: boolean) {
      if (val === null || val === undefined) return
      return yield applyPersonalCenterSetting({ device_verify_enabled: val })
    })

    const setDoubleVerify = (val: boolean) => {
      self.doubleVerify = val
    }

    const setFriendVerify = flow(function* (val: boolean) {
      if (val === null || val === undefined) return
      return yield applyPersonalCenterSetting({ friend_verify: val })
    })

    /** 在线状态：总开关 + 展示规则（1=实时，2=最近活跃），成功后由 applyPersonalCenterSetting 刷新 wallet */
    const setOnlinePresenceSettings = flow(function* (params: {
      show_online_status: boolean
      online_display_rule: 1 | 2
    }) {
      return yield applyPersonalCenterSetting(params)
    })

    const setShowOnlineStatus = (val: boolean) => {
      self.showOnlineStatus = val
    }
    // ===== 消息通知相关 =====
    const setMessageNotify = flow(function* (val: boolean) {
      if (val === null || val === undefined) return
      return yield applyPersonalCenterSetting({ msg_notify_enabled: val })
    })
    const setNotifyPreview = flow(function* (val: boolean) {
      if (val === null || val === undefined) return
      return yield applyPersonalCenterSetting({ msg_preview_enabled: val })
    })
    const setGroupNotify = flow(function* (val: boolean) {
      if (val === null || val === undefined) return
      return yield applyPersonalCenterSetting({ group_notify_enabled: val })
    })
    const setChannelNotify = flow(function* (val: boolean) {
      if (val === null || val === undefined) return
      return yield applyPersonalCenterSetting({ channel_notify_enabled: val })
    })
    const setSoundNotify = flow(function* (val: boolean) {
      if (val === null || val === undefined) return
      return yield applyPersonalCenterSetting({ sound_enabled: val })
    })
    const setVibrationNotify = flow(function* (val: boolean) {
      if (val === null || val === undefined) return
      return yield applyPersonalCenterSetting({ vibrate_enabled: val })
    })
    const setDoNotDisturb = flow(function* (val: boolean) {
      if (val === null || val === undefined) return
      const { walletStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const payload: Record<string, string | number | boolean> = { dnd_enabled: val }
      if (val) {
        const raw = walletStore.dnd_time_slot
        const slot =
          typeof raw === "number" && raw >= 1 && raw <= 6 ? raw : 1
        payload.dnd_time_slot = slot
      }
      return yield applyPersonalCenterSetting(payload)
    })

    /** 保存免打扰时间段；免打扰开启时顺带带上 `dnd_enabled`，避免服务端只认组合更新 */
    const setDoNotDisturbTimeSlot = flow(function* (slot: number) {
      if (!Number.isFinite(slot) || slot < 1 || slot > 6) return
      const { walletStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      if (walletStore.dnd_enabled === true) {
        return yield applyPersonalCenterSetting({
          dnd_enabled: true,
          dnd_time_slot: slot,
        })
      }
      return yield applyPersonalCenterSetting({ dnd_time_slot: slot })
    })
    // ===== 聊天设置 =====
    const setEnterToSend = (val: boolean) => {
      self.enterToSend = val
    }
    const setTypingStatus = (val: boolean) => {
      self.typingStatus = val
    }
    const setReadReceipt = (val: boolean) => {
      self.readReceipt = val
    }
    const setEnableEmoji = (val: boolean) => {
      self.enableEmoji = val
    }
    const setEnableSticker = (val: boolean) => {
      self.enableSticker = val
    }
    const setMessageSelfDestruct = flow(function* (val: boolean) {
      if (val === null || val === undefined) return
      return yield applyPersonalCenterSetting({ msg_destruct_enabled: val })
    })

    /** 保存消息自毁时间预设档位（与 `msg_destruct_option` 1～6 对应）；开启自毁时一并带上开关，与服务端更新习惯一致 */
    const setMessageDestructOption = flow(function* (option: number) {
      if (!Number.isFinite(option) || option < 1 || option > 6) return
      const { walletStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      if (walletStore.msg_destruct_enabled === true) {
        return yield applyPersonalCenterSetting({
          msg_destruct_enabled: true,
          msg_destruct_option: option,
        })
      }
      return yield applyPersonalCenterSetting({ msg_destruct_option: option })
    })

    return {
      setLoginNotify,
      setDeviceVerify,
      setDoubleVerify,
      setFriendVerify,
      setOnlinePresenceSettings,
      setShowOnlineStatus,
      setMessageNotify,
      setNotifyPreview,
      setGroupNotify,
      setChannelNotify,
      setSoundNotify,
      setVibrationNotify,
      setDoNotDisturb,
      setDoNotDisturbTimeSlot,
      setEnterToSend,
      setTypingStatus,
      setReadReceipt,
      setEnableEmoji,
      setEnableSticker,
      setMessageSelfDestruct,
      setMessageDestructOption,
    }
  })

export interface SettingStore extends Instance<typeof SettingStoreModel> {}
